import React, { forwardRef } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import formatPhoneForTitleBar from '../../../utils/formatPhoneForUser';
import debounce from 'lodash/debounce';

import axios from 'axios';

import formatPhoneOrEmailBasic from '../../../utils/formatPhoneOrEmailBasic';

import getSearchUrl from '../../../utils/getSearchUrl';
import { goToCustomerConversation } from '../../../utils/goToCustomerConversation';
import { buildCanonicalCustomerConversationPath } from '@/utils/customerConversationRoutes';
import { Mail, Smartphone, Loader2, Search } from 'lucide-react';
import {
	CommandDialog,
	CommandGroup,
	CommandInput,
	CommandList,
	CommandItem,
} from '@/components/ui/command';
import { UserPlus } from 'lucide-react';
import { CustomerDialog } from '@/components/runner/customer/CustomerDialog';

const isNumeric = (input) => {
	return input - 0 == input && ('' + input).trim().length > 0;
};

const CustomerLookup = forwardRef(function CustomerLookup(props) {
	const navigate = useNavigate();
	const [inputValue, setInputValue] = React.useState('');
	const [options, setOptions] = React.useState([]);
	const [errorMessage, setErrorMessage] = React.useState(''); // eslint-disable-line @typescript-eslint/no-unused-vars
	const [location, setLocation] = React.useState('');
	const [isSearching, setIsSearching] = React.useState(false);
	const [isLoading, setIsLoading] = React.useState(false);
	const [loadingMessage, setLoadingMessage] = React.useState(
		'Creating customer...'
	);
	const [customerProperties, setCustomerProperties] = React.useState([]); // eslint-disable-line @typescript-eslint/no-unused-vars

	// State for CustomerDialog
	const [showCustomerDialog, setShowCustomerDialog] = React.useState(false);
	const [customerFormData, setCustomerFormData] = React.useState({
		name: '',
		phone: '',
		email: '',
		details: '',
		_properties: [],
		billingEmail: '',
		billingAddress: '',
		tags: [],
		associatedLinks: [],
		errorMessage: '', // Add errorMessage to pass to form
	});

	const auth = useSelector((state) => state.auth);
	const businessGroup = useSelector((state) => state.businessGroup);

	// update input value when initialValue changes
	React.useEffect(() => {
		if (props.initialValue) {
			setInputValue(props.initialValue);
			handleCreateNewCustomer(props.initialValue);
		}
	}, [props.initialValue]);

	// Reset search state when both dialogs are closed. Do not reset while
	// CustomerDialog is open — handleCreateNewCustomer closes the command
	// palette first, and clearing customerFormData here was wiping prefills.
	React.useEffect(() => {
		if (!props.customerDialogIsOpen && !showCustomerDialog) {
			setIsLoading(false);
			setIsSearching(false);
			setLoadingMessage('Creating customer...');
			setInputValue('');
			setOptions([]);
			setErrorMessage('');
			setCustomerFormData({
				name: '',
				phone: '',
				email: '',
				details: '',
				_properties: [],
				billingEmail: '',
				billingAddress: '',
				tags: [],
				associatedLinks: [],
				errorMessage: '',
			});
		}
	}, [props.customerDialogIsOpen, showCustomerDialog]);

	// handleLocationChange removed - location handled by CreateCustomerForm

	const formatPhoneEmailInput = (i) => {
		const input = i;
		if (!input) {
			return '';
		}

		// check if user input is a phone number
		if (isNumeric(input.charAt(0)) || input.charAt(0) === '(') {
			return props.isActivated
				? formatPhoneForTitleBar(input)
				: 'XXX XXX XXXX';
		}

		// otherwise its an email
		return input;
	};

	const onPhoneChange = async (value) => {
		const phoneEmail = formatPhoneEmailInput(value);

		// No longer need to set phone state

		if (phoneEmail.includes('@') || phoneEmail.length === 12) {
			try {
				// check if this number has received a review invite before from this business
				const res = await axios.post(
					`/api/reviews/has-already-received-review-invitation`,
					{ phone: phoneEmail }
				);

				if (res.data.error && res.data.message) {
					setErrorMessage(res.data.message);
				}

				// if response includes a lastSent date, warn the user
				if (res.data.lastSent) {
					const { leftReview } = res.data;
					const timeAgo = moment(res.data.lastSent).fromNow();

					const timeMessage = `Review Invitation sent to ${phoneEmail} ${timeAgo}`;

					const errorMessage = leftReview
						? timeMessage
						: `${timeMessage}, but has not yet left a review`;

					setErrorMessage(errorMessage);
				}
			} catch {
				// Ignore error and clear message
				setErrorMessage('');
			}
		} else {
			setErrorMessage('');
		}
	};

	const setCustomerPhone = (newPhone) => {
		const formattedPhone = formatPhoneOrEmailBasic(newPhone);
		onPhoneChange(formattedPhone);
	};

	const handleCreateNewCustomer = (inputValueNew) => {
		const safeSearchTerm = inputValueNew.replace(/[\W_]+/g, '');
		const isNum = /^\d+$/.test(safeSearchTerm);

		// Initialize the form data based on the input
		if (isNum || inputValueNew.includes('@')) {
			// Input is phone or email
			const formatted = formatPhoneOrEmailBasic(inputValueNew);
			setCustomerFormData((prev) => ({
				...prev,
				name: '',
				phone: inputValueNew.includes('@') ? '' : formatted,
				email: inputValueNew.includes('@') ? formatted : '',
			}));
			setCustomerPhone(formatted);
		} else {
			// Input is a name
			setCustomerFormData((prev) => ({
				...prev,
				name: inputValueNew,
				phone: '',
				email: '',
			}));
			setCustomerPhone('');
		}

		// Close the command dialog and open the customer dialog
		props.handleCustomerDialogIsOpen(false);
		setShowCustomerDialog(true);
		setInputValue('');
	};

	// handleNameChange and handlePhoneChange removed - handled by CreateCustomerForm

	// debounce makes it so that the function is only called once every 400ms
	const fetch = React.useCallback(
		debounce(async (input, callback) => {
			try {
				const res = await axios(
					`${getSearchUrl(window.location.href)}/api/customer-search`,
					{
						method: 'post',
						data: {
							searchTerm: input,
							isEnterpriseRequested:
								auth.user.isEnterpriseEnabled,
						},
						withCredentials: true,
					}
				);

				callback(res.data);
			} catch {
				// Ignore error and return empty results
				callback([]);
			}
		}, 400),
		[]
	);

	React.useEffect(() => {
		let active = true;

		if (inputValue === '') {
			setOptions([]);
			setIsSearching(false);
			return;
		}

		// Check if we have enough characters to trigger a search
		const safeSearchTerm = inputValue.replace(/[\W_]+/g, '');
		const isNum = /^\d+$/.test(safeSearchTerm);

		// Only search if we have enough characters
		if (
			(!isNum && safeSearchTerm.length > 2) ||
			(isNum && safeSearchTerm.length > 2)
		) {
			// Set searching state BEFORE calling fetch
			setIsSearching(true);

			fetch(inputValue, (results) => {
				if (!active) return;

				// Always set searching to false when results come back
				setIsSearching(false);

				if (!results || !results.matchingCustomers) {
					setOptions([]);
					return;
				}
				// remove fb, google, insta results
				const filteredResults = results.matchingCustomers.filter(
					(c) => c.phone || c.email
				);

				setOptions(filteredResults);
			});
		} else {
			// Not enough characters to search, just clear options without loading state
			setOptions([]);
			setIsSearching(false);
		}

		return () => {
			active = false;
		};
	}, [inputValue, fetch]);

	const resetInputs = () => {
		setInputValue('');
		setLocation('');
		setIsSearching(false);
		setIsLoading(false);
		setLoadingMessage('Creating customer...');
		setShowCustomerDialog(false);
		setCustomerFormData({
			name: '',
			phone: '',
			email: '',
			details: '',
			_properties: [],
			billingEmail: '',
			billingAddress: '',
			tags: [],
			associatedLinks: [],
			errorMessage: '',
		});
		setCustomerProperties([]);
	};

	const getOptionIcon = (option) => {
		if (option.phone) {
			return <Smartphone className="size-7" />;
		}

		if (option.email) {
			return <Mail className="size-7" />;
		}
	};

	// Handler for CustomerDialog
	const handleCreateCustomer = async (data) => {
		setIsLoading(true);
		setLoadingMessage('Creating customer...');

		// Prepare the customer data for saving
		const customerData = {
			name: data.name,
			phoneEmail: data.phone || data.email,
			phone: data.phone,
			email: data.email,
			details: data.details,
			_location: location || auth.user._business,
			tags: data.tags,
			billingEmail: data.billingEmail,
			billingAddress: data.billingAddress,
			_properties: data._properties,
		};

		try {
			// Create the customer via API
			const response = await axios.post('/api/create-customer', {
				customer: customerData,
			});

			if (props.saveCustomer && response.data.customer?._id) {
				// The parent attaches the customer (e.g. a pipeline item), so
				// stay where we are instead of opening the conversation.
				const { customer } = response.data;
				setLoadingMessage('Adding customer...');
				await props.saveCustomer({
					_id: customer._id,
					name: customer.name,
					phone: customer.phone,
					email: customer.email,
					details: customer.details,
					_location: customer._business,
				});
				props.handleCustomerDialogIsOpen(false);
			} else if (response.data.customerConversation) {
				const { customerConversation } = response.data;

				// Update loading message
				setLoadingMessage('Customer created! Opening conversation...');

				// Add conversation to store if the parent has this method
				if (props.addConversationToCustomerConversations) {
					props.addConversationToCustomerConversations(
						customerConversation,
						true
					);
				}

				// Small delay to show success message
				await new Promise((resolve) => setTimeout(resolve, 500));

				// Navigate to the newly created conversation
				navigate(
					buildCanonicalCustomerConversationPath({
						_customerConversation: customerConversation._id,
						_business: customerConversation._business,
					})
				);

				// Focus the chat input after navigation
				setTimeout(() => {
					const el = document.getElementById('chatBoxTextBox');
					if (el) {
						el.focus();
					}
				}, 100);
			} else {
				const message =
					response.data.message ||
					'Sorry, we could not create your customer. Please refresh the page and try again.';
				alert(message);
			}
		} catch (error) {
			console.error('Error creating customer:', error);
			alert('Error creating customer. Please try again.');
		} finally {
			setIsLoading(false);
			setLoadingMessage('Creating customer...');
			// Reset and close
			resetInputs();
			setShowCustomerDialog(false);
		}
	};

	// renderNameAndPhoneInputs removed - replaced by CustomerDialog

	const highlightMatches = (text, weight) => {
		// get the index of where the highlighting should start
		const index = text.toLowerCase().indexOf(inputValue.toLowerCase());

		if (index === -1) {
			return text;
		}

		// get text before highlight
		const start = text.slice(0, index).split('');

		// get highlighted text
		const highlight = text
			.slice(index, index + inputValue.length)
			.split('');

		// get text after highlight
		const end = text.slice(start.length + highlight.length).split('');

		const startSpan = start.map((t, i) => (
			<span key={`${t}${i}`}>{t}</span>
		));
		const highlightSpan = highlight.map((t, i) => (
			<span
				key={`${i}${t}`}
				style={{
					fontSize: weight === 'HEAVY' ? 17 : 16,
					fontWeight: 'bold',
				}}
			>
				{t}
			</span>
		));
		const endSpan = end.map((t, i) => <span key={`${t}-${i}`}>{t}</span>);

		const all = [...startSpan, ...highlightSpan, ...endSpan];

		return all;
	};

	const contactInfo = (option) => {
		if (option.phone) {
			return highlightMatches(
				formatPhoneOrEmailBasic(option.phone),
				'LIGHT'
			);
		}

		if (option.email) {
			return option.email;
		}
	};

	const getBusinessName = (_business) => {
		const filteredBusiness = businessGroup.filter(
			(b) => b._id === _business
		);

		if (filteredBusiness.length) {
			return filteredBusiness[0].name;
		}

		return '';
	};

	const sortedOptions = React.useMemo(() => {
		return [...options].sort((a, b) => {
			const aTime = a.latestMessageDate
				? new Date(a.latestMessageDate).getTime()
				: 0;
			const bTime = b.latestMessageDate
				? new Date(b.latestMessageDate).getTime()
				: 0;

			return bTime - aTime;
		});
	}, [options]);

	return (
		<>
			<CommandDialog
				open={props.customerDialogIsOpen}
				onOpenChange={() => {
					resetInputs();
					props.handleCustomerDialogIsOpen(
						!props.customerDialogIsOpen
					);
				}}
				modal={props.modal}
				className="max-h-[80vh]"
				commandProps={{ shouldFilter: false }}
			>
				<>
					<div data-onboarding-guide-slot="conversation" />
					<CommandInput
						data-onboarding="inbox-customer-search"
						value={inputValue}
						onValueChange={(value) => setInputValue(value)}
						placeholder="Enter a name, phone, email, or details"
					/>
					<CommandList>
						{/* Create customer section - always show when there's non-empty input */}
						{!options.filter(
							(o) => o.phone === `+1${inputValue.trim()}`
						).length &&
							inputValue.trim().length > 0 && ( // remove the option to create a new customer if the user has entered a phone number that matches one of the returned values
								<div className="border-b border-gray-200 p-0 mb-1">
									<CommandGroup>
										<CommandItem
											// className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden aria-selected:bg-accent aria-selected:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50"
											key={inputValue}
											value={inputValue}
											onSelect={() => {
												handleCreateNewCustomer(
													inputValue.trim()
												);
											}}
											className="bg-blue-50/50 hover:bg-blue-100/50 transition-colors"
										>
											<div className="flex space-x-1 items-center">
												<div className="p-1 bg-blue-500/10 rounded-full mr-2">
													<UserPlus className="w-5 h-5 text-blue-600" />
												</div>
												<p className="font-medium text-gray-900">
													Create customer:
												</p>
												<p className="text-gray-600">
													{inputValue.trim() &&
													isNumeric(inputValue.trim())
														? `with phone ${formatPhoneOrEmailBasic(
																inputValue.trim()
														  )}`
														: inputValue.trim()}
												</p>
											</div>
										</CommandItem>
									</CommandGroup>
								</div>
							)}

						{/* Search Results Section - consistent location for all search states */}
						<div className="min-h-[300px]">
							{/* Initial state when no search term entered */}
							{!isSearching && inputValue.trim().length === 0 && (
								<div className="px-3 py-8">
									<div className="flex flex-col items-center justify-center space-y-3 text-gray-400">
										<Search className="h-8 w-8 text-gray-300" />
										<div className="text-sm">
											Start typing to search
										</div>
										<div className="text-xs">
											Search by name, phone, email, or
											details
										</div>
									</div>
								</div>
							)}

							{/* Loading state */}
							{isSearching && (
								<div className="px-3 py-8">
									<div className="flex flex-col items-center justify-center space-y-3 text-gray-500">
										<Loader2 className="h-8 w-8 animate-spin text-gray-400" />
										<div className="text-sm font-medium">
											Searching customers...
										</div>
										<div className="text-xs text-gray-400">
											Looking for &ldquo;
											{inputValue.trim()}&rdquo;
										</div>
									</div>
								</div>
							)}

							{/* Not enough characters to search */}
							{!isSearching &&
								inputValue.trim().length > 0 &&
								inputValue.trim().length <= 2 &&
								options.length === 0 && (
									<div className="px-3 py-8">
										<div className="flex flex-col items-center justify-center space-y-3 text-gray-400">
											<Search className="h-8 w-8 text-gray-300" />
											<div className="text-sm">
												Keep typing...
											</div>
											<div className="text-xs">
												Type at least 3 characters to
												search
											</div>
										</div>
									</div>
								)}

							{/* No results state (after search) */}
							{!isSearching &&
								inputValue.trim().length > 2 &&
								options.length === 0 && (
									<div className="px-3 py-8">
										<div className="flex flex-col items-center justify-center space-y-3 text-gray-400">
											<Search className="h-8 w-8 text-gray-300" />
											<div className="text-sm">
												No customers found
											</div>
											<div className="text-xs">
												Try a different search term or
												create a new customer above
											</div>
										</div>
									</div>
								)}

							{/* Results - only show CommandGroup when we have actual results */}
							{!isSearching && sortedOptions.length > 0 && (
								<CommandGroup
									heading={`Search results (${sortedOptions.length})`}
								>
									{sortedOptions.map((option) => {
										return (
											<CommandItem
												data-onboarding="inbox-customer-result"
												key={option._id}
												value={`
											${option.name}
											${option.phone}
											${option.email}`}
												onSelect={async () => {
													if (props.saveCustomer) {
														// The parent attaches the customer
														// (e.g. a pipeline item): no navigation.
														setIsLoading(true);
														setLoadingMessage(
															'Adding customer...'
														);
														try {
															await props.saveCustomer(
																{
																	_id: option._id,
																	name: option.name,
																	phone: option.phone,
																	email: option.email,
																	details:
																		option.details,
																	_location:
																		option._business,
																}
															);
														} finally {
															setIsLoading(false);
															props.handleCustomerDialogIsOpen(
																false
															);
														}
														return;
													}

													// Show loading state
													setIsLoading(true);
													setLoadingMessage(
														'Opening conversation...'
													);

													// Get conversation ID and navigate for existing customer
													try {
														await goToCustomerConversation(
															{
																customerId:
																	option._id,
																navigate,
															}
														);

														// Focus the chat input after navigation
														setTimeout(() => {
															const el =
																document.getElementById(
																	'chatBoxTextBox'
																);
															if (el) {
																el.focus();
															}
														}, 100);
													} catch (error) {
														console.error(
															'Error navigating to conversation:',
															error
														);
														setIsLoading(false);
														setLoadingMessage(
															'Creating customer...'
														);
													}

													props.handleCustomerDialogIsOpen(
														false
													);
												}}
											>
												<div className="flex space-x-2 items-center">
													<div>
														{getOptionIcon(option)}
													</div>
													<div className="flex flex-col">
														<p className="font-medium">
															{option.name}
														</p>
														<p className="text-sm text-muted-foreground">
															{option.details
																? ` (${option.details})`
																: ''}
														</p>
														<p className="text-sm text-muted-foreground">
															{contactInfo(
																option
															)}
														</p>
														{auth.user
															.isEnterpriseEnabled && (
															<p className="text-sm text-muted-foreground">
																{getBusinessName(
																	option._business
																)}
															</p>
														)}
													</div>
												</div>
											</CommandItem>
										);
									})}
								</CommandGroup>
							)}
						</div>
					</CommandList>
					{/* Loading Overlay for search view */}
					{isLoading && (
						<div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
							<div className="flex flex-col items-center space-y-4 p-8 bg-background rounded-lg shadow-lg border">
								<Loader2 className="h-12 w-12 animate-spin text-primary" />
								<p className="text-lg font-medium text-center animate-in fade-in-0 slide-in-from-bottom-2 duration-300">
									{loadingMessage}
								</p>
								<p className="text-sm text-muted-foreground text-center">
									Please wait...
								</p>
							</div>
						</div>
					)}
				</>
			</CommandDialog>

			{/* Customer Dialog for creating customers */}
			<CustomerDialog
				open={showCustomerDialog}
				setOpen={setShowCustomerDialog}
				customerInfo={customerFormData}
				createCustomerAndLinkToProject={handleCreateCustomer}
				_project={undefined}
			/>
		</>
	);
});

export default CustomerLookup;
